import { useEffect, useState } from 'react';
import { CheckCircleOutline } from '@mui/icons-material';
import { Box, ImageList, ImageListItem, Typography } from '@mui/material';

// Hooks and services
import { uploadImages } from '../../../../services/images.service';
import { uploadOnAssetDevices } from '../../../../services/inventory.services';

// Types
import { Image, ImageUploadResponse } from '../../../../types/Images';
import { UploadImageRequest } from '../../../../requestModels/uploadImageRequest';
import { AllowedFileType } from '../../../../constants/Constants';
import { DeviceImportSummary } from '../../../../types/device-import-summary';

// Components
import CircularProgressWithLabel from './CircularProgressWithLabel';

const backDrop = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0, .5)',
};

export const ProgressItem = (props: {
  param: UploadImageRequest;
  onUpdate: (uploadedFiles: any) => void;
  fileType: AllowedFileType; // Added fileType prop to distnguish between image and files
  setIsLoading: (isLoading: boolean) => void; // Added setter function for loading state
  uploadAsync?: (file: File, onUploadProgress: (progressEvent: ProgressEvent) => void) => Promise<any>;
}) => {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadRequests, setUploadRequest] = useState<UploadImageRequest[]>([]);

  useEffect(() => {
    if (props.param.fileProcess) {
      setUploadRequest(
        props.param.fileProcess
          .filter((v) => !v.image)
          .map((file) => {
            return {
              subjectId: props.param?.subjectId,
              resourceType: props.param?.resourceType,
              subjectIdentifier: props.param.subjectIdentifier,
              fileProcess: file,
            };
          }),
      );
    }
  }, [props.param.fileProcess]);

  const handleFileUpload = async (uploadRequests: UploadImageRequest[]) => {
    try {
      props.setIsLoading(true);
      setIsUploading(true);

      for (const req of uploadRequests) {
        // For image uploads, pass all parameters
        if (props.fileType === 'image') {
          const response: ImageUploadResponse = await uploadImages(
            {
              subjectId: req.subjectId,
              subjectIdentifier: req.subjectIdentifier,
              resourceType: req.resourceType,
              images: req.fileProcess,
            },
            (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(percentCompleted);
            },
          );

          props.onUpdate(response.images);
        } else {
          // use injected uploadAsync if provided, otherwise fall back to uploadOnAssetDevices
          const uploadFn = props.uploadAsync ?? uploadOnAssetDevices;
          const response = await uploadFn(req.fileProcess, (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
          });
          props.onUpdate(response);
        }
      }
    } catch (err) {
      console.error('Error uploading file', err);
    } finally {
      setIsUploading(false);
      props.setIsLoading(false);
    }
  };

  useEffect(() => {
    if (uploadRequests.length > 0) {
      setIsUploading(true);
      handleFileUpload(uploadRequests);
    }
  }, [uploadRequests]);

  return (
    <>
      {uploadRequests.map((p, index) => (
        <ImageList
          key={index}
          rowHeight={140}
          gap={4}
          cols={4}
          sx={{
            '&.MuiImageList-root': {
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))!important',
            },
          }}
        >
          {isUploading && (
            <ImageListItem cols={1} rows={1}>
              {props.fileType === 'image' ? (
                <img
                  src={URL.createObjectURL(p.fileProcess)}
                  alt='gallery'
                  loading='lazy'
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                  <Typography>{p.fileProcess.name}</Typography>
                </Box>
              )}
              <Box sx={backDrop}>
                {progress < 100 ? (
                  <CircularProgressWithLabel value={progress} />
                ) : (
                  <CheckCircleOutline sx={{ width: 60, height: 60, color: 'lightgreen' }} />
                )}
              </Box>
            </ImageListItem>
          )}
        </ImageList>
      ))}
    </>
  );
};
